<template>
    <div>
        <h1>Anleitung</h1>
        Das Benennen von Organischen Stoffen folgt immer einem klaren Plan. Hier
        wird Schritt für Schritt erklärt, wie man von diesem Molekül auf seinen
        Namen <code>2,7-Dimethyl-4-Propyl-Non-3,6-dien</code> kommt:<br />
        <img
            src="@/assets/img/benennung/anleitung/molekuel.png"
            alt="Anhand dieses Moleküls behandeln wir die allgemeine Benennung."
            title="Anhand dieses Moleküls behandeln wir die allgemeine Benennung."
        />
        <hr class="chapter-split" />
        <chapter-heading title="Reihenfolge"></chapter-heading>
        <ol>
            <li>Finden und Benennen der längsten Kohlenstoffkette</li>
            <li>
                Notieren der Nebengruppen, deren Häufigkeiten und Positionen
            </li>
            <li>Alphabetische Sortierung</li>
            <li>Anpassungen durch Doppel- oder Dreifachbindungen</li>
        </ol>
        <hr class="chapter-split" />
        <chapter-heading
            title="1. Finden und Benennen der längsten Kohlenstoffkette"
        ></chapter-heading>
        Zunächst muss die längste Kohlenstoffkette gefunden werden. Sie dient
        als Basis für den Namen. Die Kohlenstoffkette darf auch Knicke
        beinhalten. In unserem Beispiel-Molekül ist die längste Kette
        <span style="color: #f00;">rot</span> markiert:<br />
        <img
            src="@/assets/img/benennung/anleitung/schritt-1.png"
            alt="Die längste Kohlenstoffkette ist rot markiert."
            title="Die längste Kohlenstoffkette ist rot markiert."
        />
        Die längste Kette hat die Länge 9, somit merken wir uns als bisherigen
        Namen <code>Nonan</code>.
        <hr class="chapter-split" />
        <chapter-heading
            title="2. Notieren der Nebengruppen, deren Häufigkeiten und Positionen"
        ></chapter-heading>
        Wir haben zwei verschiedene Nebengruppen in unserem Molekül: 2x
        <span style="color: #00f;">Methyl</span> und 1x
        <span style="color: #0f0;">Propyl</span>. Warum <em>Methyl</em> bzw.
        <em>Proyl</em> statt <em>Methan</em> bzw <em>Propan</em>? Ganz einfach:
        Die Nebengruppen haben ein H-Atom weniger, das sich ein C-Atom ja an das
        C-Atom der längsten Kette bindet.<br />
        <img
            src="@/assets/img/benennung/anleitung/schritt-2.png"
            alt="Wir finden, zählen und benennen die Nebengruppen"
            title="Wir finden, zählen und benennen die Nebengruppen"
        />
        Nun müssen wir zählen, an welchen C-Atomen die Nebengruppen sitzen.
        Fangen wir von links an, haben wir ein Methyl an Position 2, das Propyl
        an Position 4 und das zweite Methyl an Position 7. Zählen wir jedoch von
        rechts, erhalten wir die Positionen 3 und 8 für die Methyle und 6 für
        das Propyl. Wir nehmen die Zahlen, deren Gesamtsumme die kleinste ist.
        Von links: <code>2 + 4 + 7 = 13</code>. Von rechts:
        <code>3 + 6 + 8 = 17</code>. Da <code>13 &lt; 17</code> müssen wir uns
        für die Zählung von links entscheiden.<br />
        Um zu verdeutlichen, wie oft eine Gruppe vorkommt, wird noch jeweils die
        griechische Vorsilbe für die Anzahl davorgehängt.<br />
        Unser aktueller Name lautet: <code>2,7-Di-Methyl 4-Propyl - Nonan</code>
        <hr class="chapter-split" />
        <chapter-heading title="3. Alphabetische Sortierung"></chapter-heading>
        Damit der gesamt Name noch etwas übersichtlicher ist und der Prozess der
        Benennung etwas komplizierter wird, müssen wir die Nebengruppen
        alphabetisch Sortieren. Zum Glück kommt <em>Methyl</em> im Alphabet vor
        <em>Propyl</em>, weshalb wir an unserem Namen
        <code>2,7-Di-Methyl 4-Propyl - Nonan</code> nichts ändern müssen.
        <hr class="chapter-split" />
        <chapter-heading
            title="4. Anpassungen durch Doppel- oder Dreifachbindungen"
        ></chapter-heading>
        Unglücklicherweise hat unser Molekül nicht nur Einfachbindungen, sondern
        auch Doppelbindungen. Diese sind im Bild
        <span style="color: #f00;">rot</span> gefärbt:<br />
        <img
            src="@/assets/img/benennung/anleitung/schritt-3.png"
            alt="Wir finden die Doppelbindungen"
            title="Wir finden die Doppelbindungen"
        />
        Da es Doppelbindungen sind, müssen wir den Stammnamen auf
        <code>Nonen</code> anpassen. Nun müssen wir noch die Doppelbindungen
        zählen und lokalisieren. Wir übernehmen die Zählrichtung, die wir oben
        bestimmt haben (in diesem Fall von links nach rechts) und zählen an
        welcher Bindung eine Doppelbindung vorliegt. Hier sind die Bindungen 3
        und 6 Doppelbindungen. Da es zwei Stück sind, benötigen wir die Vorsilbe
        <em>di</em>. Das führt dazu, dass unser Stammname auf
        <code>Non-3,6-di-en</code> aktualisiert wird.<br />
        Setzt man nun diese Informationen ein, erhalten wir den Namen
        <code>2,7-Dimethyl-4-Propyl-Non-3,6-dien</code>. Wie du siehst sind hier
        auch ein paar Bindestriche entfernt worden. Achte immer darauf, nicht zu
        viele Bindestriche zu verwenden, aber auch nicht zu wenige. Nutze so
        viele wie nötig, um eine optimale Lesbarkeit des Namens zu erreichen.
    </div>
</template>
<script>
import ChapterHeading from '../../components/chapterHeading';
export default {
    components: { ChapterHeading },
};
</script>
